import { ScoreLogForm } from '@/components/custom/ScoreLogForm';
import { ScoreModalShell } from '@/components/custom/ScoreModalLayout';
import { ScoreActionButton } from '@/components/programming/ScoreActionButton';
import { WorkoutCreatorMetadata } from '@/components/programming/WorkoutCreatorMetadata';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useApi } from '@/hooks/useApi';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateWithWeekday } from '@/lib/utils';
import { type WorkoutAttribution, getProgrammingScoringLabel } from '@ergshoot/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Monitor, Search, Target, ThumbsUp } from 'lucide-react';
import { useState } from 'react';

// Workout type definition
interface Workout {
  id: string;
  name: string;
  description?: string;
  track_name?: string;
  scoring_type?: string;
  duration_minutes?: number;
  scheduled_date?: string;
  equipment?: string[];
  instructions?: string;
  movements?: string[];
  category?: string;
  workout_type?: string;
  attribution?: WorkoutAttribution | null;
  created_at?: string | null;
  created_by_username?: string | null;
}

interface WorkoutsResponse {
  items: Workout[];
  total: number;
  limit: number;
  offset: number;
}

const styleOptions = [
  { value: 'all', label: 'All Styles' },
  { value: 'short_sprint', label: 'Short Sprint' },
  { value: 'long_sprint', label: 'Long Sprint' },
  { value: 'middle_distance', label: 'Middle Distance' },
  { value: 'enduro_sprint', label: 'Enduro Sprint' },
  { value: 'enduro_long', label: 'Enduro Long' },
  { value: 'enduro_medley', label: 'Enduro Medley' },
];

export default function TheStandards() {
  const { api } = useApi();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [styleFilter, setStyleFilter] = useState('all');
  const [scoringWorkout, setScoringWorkout] = useState<Workout | null>(null);

  const canLogScores = hasPermission('log_scores');

  // Fetch standards workouts from API
  const { data, isLoading, error } = useQuery({
    queryKey: ['standards-workouts'],
    queryFn: async () => {
      // Fetch workouts from the standards track
      const response = await api.get<WorkoutsResponse>(
        '/programming/workouts?track_id=track-standards&limit=100'
      );
      if (!response.success) throw new Error(response.error);
      return response.data;
    },
  });

  const workouts = data?.items || [];

  // Filter workouts
  const filteredWorkouts = workouts.filter((w) => {
    const matchesSearch =
      !searchQuery ||
      w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      w.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStyle = styleFilter === 'all' || w.category === styleFilter;
    return matchesSearch && matchesStyle;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-destructive">Error loading workouts: {(error as Error).message}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">THE STANDARDS</h1>
        <p className="text-muted-foreground">ErgShoot benchmark and standard workouts</p>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Filter workouts by title or description..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          options={styleOptions}
          value={styleFilter}
          onChange={(e) => setStyleFilter(e.target.value)}
          className="w-full sm:w-[180px]"
        />
      </div>

      {/* Workouts Grid */}
      {filteredWorkouts.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Target className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {workouts.length === 0
                ? 'No standard workouts have been added yet'
                : 'No workouts match your filters'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {workouts.length === 0
                ? 'Check back later for benchmark workouts like Fran, Grace, and more.'
                : 'Try adjusting your search or filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredWorkouts.map((workout) => (
            <Card key={workout.id}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{workout.name}</CardTitle>
                    <CardDescription className="whitespace-pre-wrap">
                      {workout.description}
                    </CardDescription>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2">
                  {workout.category && (
                    <Badge variant="secondary" className="text-xs">
                      {styleOptions.find((s) => s.value === workout.category)?.label ||
                        workout.category}
                    </Badge>
                  )}
                  {workout.scoring_type && (
                    <Badge variant="destructive" className="text-xs">
                      {getProgrammingScoringLabel(workout.scoring_type)}
                    </Badge>
                  )}
                  {workout.duration_minutes && (
                    <Badge variant="secondary" className="text-xs">
                      {workout.duration_minutes} min
                    </Badge>
                  )}
                  {workout.scheduled_date &&
                    workout.scheduled_date.length >= 10 &&
                    !workout.scheduled_date.startsWith('1970') && (
                      <Badge variant="outline" className="text-xs">
                        {formatDateWithWeekday(workout.scheduled_date)}
                      </Badge>
                    )}
                </div>
                <WorkoutCreatorMetadata
                  attribution={workout.attribution}
                  created_at={workout.created_at}
                  created_by_username={workout.created_by_username}
                  className="pt-1"
                />
              </CardHeader>
              <CardContent className="space-y-4">
                {workout.equipment && workout.equipment.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Equipment</p>
                    <p className="text-sm">{workout.equipment.join(', ')}</p>
                  </div>
                )}
                {workout.movements && workout.movements.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Movements</p>
                    <p className="text-sm">{workout.movements.join(', ')}</p>
                  </div>
                )}
                {workout.instructions && (
                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">Instructions</p>
                    <p className="text-sm whitespace-pre-wrap">{workout.instructions}</p>
                  </div>
                )}
                <div className="flex items-center gap-2 pt-2">
                  {canLogScores && <ScoreActionButton onClick={() => setScoringWorkout(workout)} />}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`/tv/${workout.id}`, '_blank')}
                  >
                    <Monitor className="h-4 w-4 mr-2" />
                    TV Display
                  </Button>
                  <UpvoteButton workoutId={workout.id} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Score Log Form */}
      {scoringWorkout && (
        <ScoreModalShell
          ariaLabel={`Log score for ${scoringWorkout.name}`}
          onClose={() => setScoringWorkout(null)}
        >
          <ScoreLogForm
            workoutId={scoringWorkout.id}
            workoutName={scoringWorkout.name}
            scoringType={scoringWorkout.scoring_type || 'reps'}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['standards-workouts'] });
              setScoringWorkout(null);
            }}
            onCancel={() => setScoringWorkout(null)}
          />
        </ScoreModalShell>
      )}
    </div>
  );
}

function UpvoteButton({ workoutId }: { workoutId: string }) {
  const { api } = useApi();
  const [isVoting, setIsVoting] = useState(false);
  const [voted, setVoted] = useState(false);
  const [count, setCount] = useState(0);

  const handleVote = async () => {
    setIsVoting(true);
    try {
      const response = await api.post<{ voted: boolean; vote_count: number }>(
        `/programming/workouts/${workoutId}/vote`,
        {}
      );
      if (response.success && response.data) {
        setVoted(response.data.voted);
        setCount(response.data.vote_count);
      }
    } catch {
      // silently fail
    } finally {
      setIsVoting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleVote}
      disabled={isVoting}
      className={voted ? 'text-primary' : 'text-muted-foreground'}
    >
      <ThumbsUp className={`h-4 w-4 mr-1 ${voted ? 'fill-current' : ''}`} />
      {count > 0 ? count : ''}
    </Button>
  );
}
